/* Tiny primitives: Icon (lucide-style stroke), Avatar, TaxonomyField */
import React from 'react';
import { useAnchoredPosition, HIDDEN_ANCHORED_STYLE } from '../lib/use-anchored-position.jsx';

/* Format a percentage for display: round to 1 decimal and drop a
 * trailing ".0" so floating-point sums like 57.5999999999994 render
 * as "57.6" and clean integers stay "75". Caller appends the "%". */
export const fmtPct = n => +(+(n || 0)).toFixed(1);

const ICONS = {
  chat: 'M3 7c0-1.1.9-2 2-2h14a2 2 0 012 2v9a2 2 0 01-2 2h-9l-5 4v-4H5a2 2 0 01-2-2V7z',
  sparkle: 'M12 3l1.8 5.4L19 10l-5.2 1.6L12 17l-1.8-5.4L5 10l5.2-1.6L12 3z',
  file: 'M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z M14 3v6h6',
  book: 'M4 4.5A2.5 2.5 0 016.5 2H20v18H6.5A2.5 2.5 0 014 17.5v-13z',
  git: 'M5 3v18 M19 3v6 M5 12h10a4 4 0 004-4',
  profile: 'M16 11a4 4 0 11-8 0 4 4 0 018 0z M3 21v-2a6 6 0 016-6h6a6 6 0 016 6v2',
  shield: 'M12 2l8 4v6c0 5-3.5 9-8 10-4.5-1-8-5-8-10V6l8-4z',
  cog: 'M12 9a3 3 0 100 6 3 3 0 000-6z M19 12a7 7 0 00-.1-1.2l2.1-1.7-2-3.4-2.5 1a7 7 0 00-2.1-1.2L14 3h-4l-.4 2.6a7 7 0 00-2.1 1.2l-2.5-1-2 3.4 2.1 1.7A7 7 0 005 12c0 .4 0 .8.1 1.2l-2.1 1.7 2 3.4 2.5-1a7 7 0 002.1 1.2L10 21h4l.4-2.6a7 7 0 002.1-1.2l2.5 1 2-3.4-2.1-1.7c.1-.4.1-.8.1-1.2z',
  search: 'M11 19a8 8 0 110-16 8 8 0 010 16z M21 21l-4.3-4.3',
  bell: 'M6 8a6 6 0 1112 0c0 7 3 9 3 9H3s3-2 3-9z M10 21h4',
  plus: 'M12 5v14 M5 12h14',
  check: 'M20 6L9 17l-5-5',
  paperclip: 'M21 12l-9 9a5 5 0 11-7-7l9-9a3.5 3.5 0 015 5L9 19a2 2 0 11-3-3l8-8',
  briefcase: 'M3 7h18v13H3V7z M8 7V5a2 2 0 012-2h4a2 2 0 012 2v2',
  house: 'M3 12L12 3l9 9 M5 10v10h14V10',
  reports: 'M9 17v-6 M12 17V7 M15 17v-3 M3 4h18v16H3V4z',
  time: 'M12 22a10 10 0 110-20 10 10 0 010 20z M12 6v6l4 2',
  key: 'M21 2l-9 9 M14 9l3 3m-7-1a4 4 0 11-8 0 4 4 0 018 0z',
  compass: 'M12 22a10 10 0 110-20 10 10 0 010 20z M16 8l-2 6-6 2 2-6 6-2z',
  db: 'M4 6c0-2 4-3 8-3s8 1 8 3v12c0 2-4 3-8 3s-8-1-8-3V6z M4 6c0 2 4 3 8 3s8-1 8-3 M4 12c0 2 4 3 8 3s8-1 8-3',
  edit: 'M12 20h9 M16.5 3.5a2.1 2.1 0 113 3L7 19l-4 1 1-4 12.5-12.5z',
  trash: 'M3 6h18 M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2 M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6 M10 11v6 M14 11v6',
  menu: 'M3 6h18 M3 12h18 M3 18h18',
  mic: 'M12 2a3 3 0 00-3 3v6a3 3 0 006 0V5a3 3 0 00-3-3z M5 11a7 7 0 0014 0 M12 18v3 M8 21h8',
  speaker: 'M3 9h4l5-4v14l-5-4H3V9z M16 8a5 5 0 010 8 M19 5a9 9 0 010 14',
  expand:  'M4 14v6h6 M20 10V4h-6 M4 20l7-7 M20 4l-7 7',
  /* Minimize — classic "_" inside a hint of frame at small sizes.
     Reads instantly as "make smaller / dock back". */
  collapse: 'M5 14h14',
  close: 'M18 6L6 18 M6 6l12 12',
  'arrow-left': 'M19 12H5 M12 19l-7-7 7-7',
  'log-out': 'M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4 M16 17l5-5-5-5 M21 12H9',
  'chevron-right': 'M9 6l6 6-6 6',
  'thumbs-up':   'M7 11v8a2 2 0 002 2h7.5a2 2 0 001.95-1.57L20.5 13a2 2 0 00-2-2.43H14V6a2 2 0 00-2-2l-3 7v 0z M3 11h4v10H3z',
  'thumbs-down': 'M17 13V5a2 2 0 00-2-2H7.5a2 2 0 00-1.95 1.57L3.5 11a2 2 0 002 2.43H10v5a2 2 0 002 2l3-7v 0z M21 13h-4V3h4z',
  'external-link': 'M14 3h7v7 M10 14l11-11 M21 14v5a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h5',
  send: 'M22 2L11 13 M22 2l-7 20-4-9-9-4 20-7z',
  /* Conversation — speech bubble with a smaller speech bubble inside.
     Reads as "two people talking" / "live conversation". Used by the
     chat-voice button (top-right) to launch voice mode. */
  conversation: 'M3 8a3 3 0 013-3h9a3 3 0 013 3v6a3 3 0 01-3 3h-3l-4 3v-3H6a3 3 0 01-3-3V8z M9 19h2a2 2 0 002-2v-1',
  /* Phone — universal "live call" icon. Used by the chat-voice
     button at top-right to differentiate it from the chat-history
     bubble icon at top-left. */
  phone: 'M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z',
  /* Waveform — five vertical audio bars of varying heights. Used by
     the chat-voice button to signal "live audio stream" / voice
     interface, à la Spotify or a podcast app. */
  waveform: 'M4 9v6 M8 6v12 M12 3v18 M16 6v12 M20 9v6',
  /* Refresh / loader — a clean three-quarter ring. The previous
     "circular arrow with corner arrowhead" rendered as a hook with
     a square box at the tip, which read as broken at small sizes.
     The pure ring is universally recognised as a loading spinner;
     the pull-to-refresh code rotates it as the user pulls and
     spins it continuously during refresh. */
  refresh: 'M21 12a9 9 0 11-9-9',
  /* Upload — arrow rising into a tray. Universal "attach file" /
     "upload" affordance. */
  upload: 'M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4 M17 8l-5-5-5 5 M12 3v12',
  /* Mic-off — full mic body + diagonal slash. The previous path
     was an unclosed arc which rendered as a broken half-mic. */
  'mic-off': 'M12 2a3 3 0 00-3 3v6a3 3 0 006 0V5a3 3 0 00-3-3z M5 11a7 7 0 0014 0 M12 18v3 M8 21h8 M3 3l18 18',
  'arrow-up': 'M12 19V5 M5 12l7-7 7 7',
  'arrow-down': 'M12 5v14 M19 12l-7 7-7-7',
  'chevron-down': 'M6 9l6 6 6-6',
  /* Lucide-style file-type variants. Same outer file shape so they
     read as a family; distinguishing inner glyph per type. */
  'file-text':         'M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z M14 3v6h6 M9 14h6 M9 18h4',
  'file-pdf':          'M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z M14 3v6h6 M9 14v3 M9 14h1a1 1 0 110 2H9 M13 14v3h1a1.5 1.5 0 000-3z',
  'file-image':        'M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z M14 3v6h6 M9.5 14a1 1 0 100-2 1 1 0 000 2z M16 19l-4-5-4 5',
  'file-spreadsheet':  'M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z M14 3v6h6 M8 14h8 M8 18h8 M12 13v6',
  'file-presentation': 'M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6z M14 3v6h6 M9 18v-3 M12 18v-5 M15 18v-2',
};

/* Map a mime type to the right file icon. Returns just the icon
 * name; consumer wraps it in <Icon name={…}/> at whatever size. */
export function iconNameForMime(mime) {
  if (!mime) return 'file-text';
  const m = String(mime).toLowerCase();
  if (m.startsWith('image/')) return 'file-image';
  if (m === 'application/pdf') return 'file-pdf';
  if (m.includes('spreadsheet') || m.includes('excel') || m === 'text/csv' || m.endsWith('.csv')) return 'file-spreadsheet';
  if (m.includes('presentation') || m.includes('powerpoint')) return 'file-presentation';
  if (m.includes('word') || m === 'text/markdown' || m === 'text/plain') return 'file-text';
  return 'file-text';
}

/** Tiny wrapper that picks the file-type icon for a mime + tints it
 *  if the caller wants. Stroke colour driven by `tint` so callers can
 *  swap between subtle (var(--fg-muted)) and brand (var(--orange)). */
export const MimeIcon = ({ mime, size = 18, tint = 'currentColor' }) => (
  <Icon name={iconNameForMime(mime)} size={size} stroke={tint}/>
);

export const Icon = ({ name, size = 16, stroke = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
    <path d={ICONS[name] || ICONS.sparkle}/>
  </svg>
);

/* `size` is a keyword (sm/md/lg/xl) or a raw pixel number for fine
   control (e.g. the tiny creator avatar on a run row). `title` adds a
   hover tooltip — handy where the name isn't otherwise spelled out. */
export const Avatar = ({ src, name, size = 'md', title }) => {
  const px = typeof size === 'number' ? size
    : size === 'xl' ? 96 : size === 'lg' ? 48 : size === 'sm' ? 20 : 32;
  if (src) return <img src={src} alt={name} title={title} style={{width:px,height:px,borderRadius:'50%',objectFit:'cover',flexShrink:0}}/>;
  const initials = (name || '?').split(' ').map(w=>w[0]).slice(0,2).join('');
  return <div title={title} style={{width:px,height:px,borderRadius:'50%',background:'var(--bg-card)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:px*0.36,fontWeight:600,flexShrink:0}}>{initials}</div>;
};

/* ─── Entity links ──────────────────────────────────────────────────
 * Single source of truth for deep-linking to a directory entity's
 * detail page. Every reference to a person / project / client / service
 * / industry / organization / office anywhere in the app should route
 * through these so links stay consistent — and so new surfaces get
 * clickable entities for free. All detail routes resolve by slug OR id,
 * so callers pass whichever handle they have. */
const ENTITY_ROUTES = {
  person: 'people',
  project: 'projects',
  client: 'clients',
  service: 'services',
  industry: 'industries',
  organization: 'organizations',
  office: 'offices',
};

/* Pick the best handle off an entity object (slug preferred for clean
 * URLs, falling back to the raw id). */
export const entityHandle = o => (o && (o.slug || o._id || o.id)) || null;

/* Build the hash href for an entity, or null when there's nothing to
 * link to (so callers can render plain text as a fallback). */
export function entityHref(type, handle) {
  const route = ENTITY_ROUTES[type];
  return route && handle ? `#${route}/${handle}` : null;
}

/* Inline link to an entity detail page. Renders its children as plain
 * text (no anchor) when there's no handle to resolve, so it's always
 * safe to drop in. `muted` keeps the surrounding text colour instead of
 * the orange accent — handy inside table cells / metadata. */
export const EntityLink = ({ type, handle, children, title, muted = false, className, style }) => {
  const href = entityHref(type, handle);
  if (!href) return <>{children}</>;
  return (
    <a
      href={href}
      title={title}
      className={className}
      style={{ color: muted ? 'inherit' : 'var(--orange)', textDecoration: 'none', ...style }}
    >
      {children}
    </a>
  );
};

/* Clickable person tile used across every directory detail page —
 * leads, managers, project members, and the org / office / client
 * people grids. The whole tile links to the person's page; an optional
 * `onRemove` renders a × button OUTSIDE the anchor (admin editing) so
 * removing someone never navigates. `lines` are extra muted sub-rows
 * (title, company · office); `badge` is an optional trailing chip. */
export const PersonCard = ({ userId, name, photoUrl, title, lines = [], badge, onRemove }) => {
  const href = entityHref('person', userId);
  const pad = onRemove ? 16 : 0;
  const body = (
    <>
      <Avatar src={photoUrl} name={name} size="md"/>
      <div style={{flex:1,minWidth:0,paddingRight:pad}}>
        <div style={{fontWeight:500,fontSize:13,lineHeight:1.2}}>{name}</div>
        <div style={{fontSize:11.5,color:'var(--fg-muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{title || '—'}</div>
        {lines.filter(Boolean).map((l, i) => (
          <div key={i} style={{fontSize:11,color:'var(--fg-muted)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l}</div>
        ))}
        {badge}
      </div>
    </>
  );
  return (
    <div style={{padding:'8px 2px',position:'relative'}}>
      {href ? (
        <a href={href} title={`Open ${name}`} style={{display:'flex',gap:10,alignItems:'flex-start',textDecoration:'none',color:'inherit'}}>
          {body}
        </a>
      ) : (
        <div style={{display:'flex',gap:10,alignItems:'flex-start'}}>{body}</div>
      )}
      {onRemove && (
        <button
          type="button"
          title="Remove"
          onClick={e => { e.preventDefault(); e.stopPropagation(); onRemove(); }}
          style={{position:'absolute',top:6,right:6,background:'transparent',border:0,color:'var(--fg-muted)',cursor:'pointer',padding:4,fontSize:13}}
        >×</button>
      )}
    </div>
  );
};

/**
 * Typed-autocomplete multi-select working on objects keyed by id.
 *
 * items:      Array<{ id, name, description?, parentId? }> — the catalog
 * selectedIds: string[]                                    — currently picked
 * onChange:   (nextIds) => void
 *
 * Renders the selected entries as removable chips inline, and an input
 * underneath that filters the catalog as you type. ↑/↓ to move,
 * Enter to add the highlighted item, Backspace on empty input to
 * remove the last chip. No new-entry creation here — admins seed
 * catalogs in Admin → Services/Industries/Clients.
 */
export const TaxonomyMultiSelect = ({ label, hint, items = [], selectedIds = [], onChange, emptyMessage }) => {
  const [q, setQ] = React.useState('');
  const [highlight, setHighlight] = React.useState(0);
  const [open, setOpen] = React.useState(false);
  const inputRef = React.useRef(null);
  const wrapRef = React.useRef(null);

  // Close menu on outside click. `pointerdown` fires for mouse, touch and
  // pen, so the dropdown dismisses reliably on mobile too (a plain
  // `mousedown` listener never fires on touch, which left the menu stuck
  // open on phones). The menu is a DOM descendant of wrapRef even though it
  // renders position:fixed, so clicks inside it don't count as "outside".
  React.useEffect(() => {
    const onDoc = e => { if (!wrapRef.current?.contains(e.target)) setOpen(false); };
    document.addEventListener('pointerdown', onDoc);
    return () => document.removeEventListener('pointerdown', onDoc);
  }, []);

  // The menu is position:fixed (see render) so it escapes any
  // `overflow:hidden` ancestor — without this, the dropdown was clipped
  // for the bottom-most picker on the people-detail page, where each
  // section is an overflow-hidden card. The shared hook measures the
  // wrapper, flips above the field when there's more room there, and shifts
  // it inward so it never spills off the right edge. `recomputeKey` re-runs
  // it when chips change the wrapper's height.
  const menuPos = useAnchoredPosition(wrapRef, open, {
    matchWidth: true, align: 'start', offset: 4, maxHeight: 280, flipThreshold: 280,
    recomputeKey: selectedIds.length,
  });

  const selectedSet = new Set(selectedIds);
  const selectedItems = items.filter(i => selectedSet.has(i.id));
  const query = q.trim().toLowerCase();
  // No artificial slice: the menu has its own max-height + overflow
  // scroll (see .taxo__menu styling below). Pre-slicing to 8 was the
  // cause of "bottom items only appear after picking one" — the DOM
  // only ever held 8 rows so scrolling did nothing.
  const candidates = items
    .filter(i => !selectedSet.has(i.id))
    .filter(i => !query || i.name.toLowerCase().includes(query) || (i.description || '').toLowerCase().includes(query));

  React.useEffect(() => { setHighlight(0); }, [q, items.length]);

  const add = id => {
    if (!id || selectedSet.has(id)) return;
    onChange([...selectedIds, id]);
    setQ('');
    inputRef.current?.focus();
  };
  const remove = id => onChange(selectedIds.filter(x => x !== id));

  const onKeyDown = e => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setHighlight(h => Math.min(h + 1, candidates.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (candidates[highlight]) add(candidates[highlight].id);
    }
    else if (e.key === 'Backspace' && !q && selectedIds.length > 0) {
      remove(selectedIds[selectedIds.length - 1]);
    }
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div className="taxo" ref={wrapRef}>
      {label && <label>{label}</label>}
      {hint && <div style={{fontSize:11.5,color:'var(--fg-muted)',marginBottom:6,marginTop:-2}}>{hint}</div>}

      <div className="tag-input" onClick={() => { setOpen(true); inputRef.current?.focus(); }}>
        {selectedItems.map(i => (
          <span key={i.id} className="tag tag--orange" title={i.description || undefined}>
            {i.name} <span className="x" onClick={e => { e.stopPropagation(); remove(i.id); }}>×</span>
          </span>
        ))}
        <input
          ref={inputRef}
          value={q}
          onChange={e => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={selectedItems.length === 0 ? (items.length === 0 ? (emptyMessage || 'no options yet') : 'type to search…') : '+ add'}
          disabled={items.length === 0}
        />
      </div>

      {open && items.length > 0 && (
        <div className="taxo__menu" style={{zIndex:90,overflowY:'auto',...(menuPos || HIDDEN_ANCHORED_STYLE)}}>
          {candidates.length === 0 && (
            <div style={{padding:'8px 12px',fontSize:12,color:'var(--fg-muted)'}}>
              {query ? 'no matches' : 'all options already added'}
            </div>
          )}
          {candidates.map((i, idx) => (
            <div
              key={i.id}
              className={`taxo__menu-item ${idx === highlight ? 'taxo__menu-item--on' : ''}`}
              onMouseEnter={() => setHighlight(idx)}
              onClick={() => add(i.id)}
            >
              <div style={{fontSize:13}}>{i.name}</div>
              {i.description && <div style={{fontSize:11,color:'var(--fg-muted)',marginTop:2}}>{i.description}</div>}
            </div>
          ))}
        </div>
      )}

      {items.length === 0 && emptyMessage && (
        <div style={{padding:8,fontSize:11.5,color:'var(--fg-muted)'}}>{emptyMessage}</div>
      )}
    </div>
  );
};

/**
 * Thumbs-up / thumbs-down feedback widget for any AI output.
 *
 * Submits to POST /api/feedback. Up = single click + done. Down
 * expands an inline form asking "what went wrong?" + "what should
 * have happened?" before sending. Once submitted the widget collapses
 * to a "thanks, recorded" line.
 *
 * Props:
 *   target     — { type, id, label }
 *   snapshots  — { intent, aiDid, link } optional context that helps
 *                an admin understand the row without re-opening the chat
 *   compact    — true for inline rows (news items); false for big blocks
 *   onSubmitted (optional) — fired with the saved row
 */
export const ThumbsRow = ({ target, snapshots, compact = false, onSubmitted }) => {
  const [state, setState] = React.useState('idle');   // idle | reasoning | submitting | done | error
  const [rating, setRating] = React.useState(null);   // 1 | -1
  const [reason, setReason] = React.useState('');
  const [suggestion, setSuggestion] = React.useState('');
  const [errMsg, setErrMsg] = React.useState('');

  const send = async (finalRating, finalReason, finalSuggestion) => {
    setState('submitting'); setErrMsg('');
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({
          targetType: target.type,
          targetId: target.id,
          targetLabel: target.label || null,
          rating: finalRating,
          reason: finalReason || null,
          suggestion: finalSuggestion || null,
          intentSnapshot: snapshots?.intent || null,
          aiDidSnapshot: snapshots?.aiDid || null,
          outputLink: snapshots?.link || null,
        }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || `feedback → ${res.status}`);
      setState('done');
      if (onSubmitted) onSubmitted(json.feedback);
    } catch (err) {
      setState('error');
      setErrMsg(err.message);
    }
  };

  const clickUp = () => {
    setRating(1);
    send(1, '', '');
  };
  const clickDown = () => {
    setRating(-1);
    setState('reasoning');
  };
  const submitDown = () => send(-1, reason, suggestion);

  if (state === 'done') {
    return (
      <div className="thumbs-row thumbs-row--compact" style={{fontSize: compact ? 11 : 12, color:'var(--fg-muted)'}}>
        {rating === 1 ? 'Thanks — saved.' : 'Thanks — added to Admin → Errors & Optimizations.'}
      </div>
    );
  }
  if (state === 'error') {
    return (
      <div className="thumbs-row" style={{fontSize:12,color:'#A20235'}}>
        Couldn't save feedback: {errMsg} <span style={{cursor:'pointer',textDecoration:'underline',marginLeft:6}} onClick={() => setState('idle')}>retry</span>
      </div>
    );
  }

  // Thumbs-up path: no form, just an inline saving indicator while
  // the POST is in flight. Without this we'd briefly flash the
  // "What went wrong?" form between clickUp() setting state to
  // 'submitting' and the response landing on 'done'.
  if (state === 'submitting' && rating === 1) {
    return (
      <div className="thumbs-row thumbs-row--compact" style={{fontSize: compact ? 11 : 12, color:'var(--fg-muted)'}}>
        Saving…
      </div>
    );
  }

  if (state === 'idle') {
    return (
      <div className="thumbs-row" style={{
        display:'flex',alignItems:'center',gap:8,
        fontSize: compact ? 11 : 12, color:'var(--fg-muted)',
      }}>
        <span>How was this?</span>
        <button
          onClick={clickUp}
          className="thumbs-btn"
          title="Helpful"
          style={{
            display:'inline-flex',alignItems:'center',gap:4,padding:'3px 8px',
            background:'transparent',border:'1px solid var(--border)',borderRadius:6,
            cursor:'pointer',fontSize:11,
          }}
        >
          <Icon name="thumbs-up" size={12}/> Helpful
        </button>
        <button
          onClick={clickDown}
          className="thumbs-btn"
          title="Could be better"
          style={{
            display:'inline-flex',alignItems:'center',gap:4,padding:'3px 8px',
            background:'transparent',border:'1px solid var(--border)',borderRadius:6,
            cursor:'pointer',fontSize:11,
          }}
        >
          <Icon name="thumbs-down" size={12}/> Could be better
        </button>
      </div>
    );
  }

  // reasoning | submitting
  return (
    <div className="thumbs-row" style={{
      padding:10,background:'var(--bg-alt)',borderRadius:8,
      display:'flex',flexDirection:'column',gap:8,fontSize:12,
    }}>
      <div style={{fontSize:11.5,color:'var(--fg-muted)'}}>Help us improve this output — your reason lands in Admin → Errors & Optimizations.</div>
      <textarea
        rows={2}
        placeholder="What went wrong?"
        value={reason}
        onChange={e => setReason(e.target.value)}
        style={{
          padding:'6px 8px',fontSize:12,fontFamily:'inherit',resize:'vertical',
          border:'1px solid var(--border)',borderRadius:6,background:'var(--bg)',
        }}
      />
      <textarea
        rows={2}
        placeholder="What should the AI have done instead? (optional)"
        value={suggestion}
        onChange={e => setSuggestion(e.target.value)}
        style={{
          padding:'6px 8px',fontSize:12,fontFamily:'inherit',resize:'vertical',
          border:'1px solid var(--border)',borderRadius:6,background:'var(--bg)',
        }}
      />
      <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
        <button onClick={() => setState('idle')} className="btn-secondary" style={{padding:'4px 10px',fontSize:11}} disabled={state === 'submitting'}>
          Cancel
        </button>
        <button onClick={submitDown} className="btn-orange" style={{padding:'4px 12px',fontSize:11}} disabled={state === 'submitting' || !reason.trim()}>
          {state === 'submitting' ? 'Saving…' : 'Submit'}
        </button>
      </div>
    </div>
  );
};

export const TaxonomyField = ({ label, value, options, onChange }) => {
  const [q, setQ] = React.useState('');
  const inputRef = React.useRef();
  const filtered = options.filter(o => o.toLowerCase().includes(q.toLowerCase()) && !value.includes(o)).slice(0, 6);
  const isNew = q.trim() && !options.find(o => o.toLowerCase() === q.toLowerCase().trim());

  const add = (v) => { onChange([...value, v]); setQ(''); inputRef.current?.focus(); };
  const remove = (v) => onChange(value.filter(x => x !== v));

  return (
    <div className="taxo">
      <label>{label}</label>
      <div className="tag-input">
        {value.map(v => (
          <span key={v} className="tag tag--orange">{v} <span className="x" onClick={() => remove(v)}>×</span></span>
        ))}
        <input
          ref={inputRef}
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter' && q.trim()) { add(q.trim()); e.preventDefault(); } }}
          placeholder={value.length === 0 ? 'Search or add new…' : '+ add'}
        />
      </div>
      {q && (
        <div className="taxo__menu">
          {filtered.map(o => (
            <div key={o} className="taxo__menu-item" onClick={() => add(o)}>{o}</div>
          ))}
          {isNew && (
            <div className="taxo__menu-item taxo__menu-item--new" onClick={() => add(q.trim())}>
              <Icon name="plus" size={12}/> Add "<strong>{q.trim()}</strong>" — becomes available firm-wide
            </div>
          )}
          {filtered.length === 0 && !isNew && <div style={{padding:'8px 12px',fontSize:12,color:'var(--fg-muted)'}}>no matches</div>}
        </div>
      )}
    </div>
  );
};
